import { CreateStakingErc3643PoolResult, StakingErc3643CreateOnChainResult } from 'Modules'
import type { Address } from 'viem'

import abiPTOOrchestratorErc3643 from 'abi/abi-pto-orchestrator-erc3643.json'
import { stakingErc3643Api } from 'api/staking-erc3643-api'
import { isSameAddress } from 'extends/pto-v3.extend'
import { toPoolStakingParams } from 'extends/staking-erc3643.extend'
import { useContractViem, writeContractAndWait } from 'hooks/contract/use-contract-viem'
import { ORCHESTRATOR_NOT_CONFIGURED, usePtoOrchestrator } from 'hooks/pto-v3/use-pto-orchestrator'
import { clientWallet, publicWallet } from 'libs/viem/config'
import { logError, logInfo } from 'utils/logger'

const isSameAddressLoose = (a?: string | null, b?: string | null) =>
  a && b && a.toLowerCase() === b.toLowerCase()

/**
 * Create staking pool (ERC-3643, fixed reward) — API + contract flow:
 *
 * 1. POST /admin/staking-erc3643/pools with rewardPerYear in whole reward tokens. BE stores the
 *    pool and returns StakingPoolParams (rewardPerYear already in raw units), the predicted vault
 *    identity / staking address and the aprAgent call.
 * 2. The connected wallet (orchestrator owner or agent) sends
 *    PTOOrchestrator.createStakingPool(StakingPoolParams). In that one tx the orchestrator deploys
 *    the vault ONCHAINID + claim and the vault, registers the vault, then has
 *    FixedRewardStakingFactory deploy the pool — so only the orchestrator ABI is needed here.
 *    Simulated first: the claim is signed for the predicted identity, so a different address
 *    aborts before anything is sent.
 * 3. aprAgent: pool.addAgent(REF_SIGNER). Owner-only, so sent only when the wallet is
 *    initialOwner. It lets BE apply later edits (rate / Start / End) on-chain without an admin
 *    signature; if skipped or failed, edits stay in DB and BE reports aprSyncError
 *    REF_SIGNER_NOT_AGENT.
 */
export const useCreateStakingPool3643 = () => {
  const { orchestratorAddress, isAgent } = usePtoOrchestrator()
  const orchestrator = useContractViem(
    orchestratorAddress as Address | null,
    abiPTOOrchestratorErc3643
  )

  const deployCreatePoolStaking = async (
    account: Address,
    args: CreateStakingErc3643PoolResult
  ): Promise<StakingErc3643CreateOnChainResult> => {
    if (!orchestratorAddress || !orchestrator) {
      throw new Error(ORCHESTRATOR_NOT_CONFIGURED)
    }

    const params = toPoolStakingParams(args)
    logInfo('Staking ERC-3643 createStakingPool params:', params)

    // PTOOrchestrator upgrade (2026-10-06): createPoolStaking was renamed createStakingPool
    // PTOOrchestrator upgrade (2026-10-20): pools are FixedRewardStaking — no oracle / price
    // sources, apr (uint64) replaced by rewardPerYear (uint128)
    const { request, result } = await orchestrator.simulate.createStakingPool([params], {
      account,
    })
    const [vaultAddress, vaultIdentity, stakingAddress] = result as [Address, Address, Address]

    if (args.predictedVaultIdentity && !isSameAddress(args.predictedVaultIdentity, vaultIdentity)) {
      throw new Error(
        `Vault identity mismatch: expected ${args.predictedVaultIdentity}, got ${vaultIdentity}`
      )
    }
    if (
      args.predictedStakingAddress &&
      !isSameAddress(args.predictedStakingAddress, stakingAddress)
    ) {
      throw new Error(
        `Staking address mismatch: expected ${args.predictedStakingAddress}, got ${stakingAddress}`
      )
    }

    const { hash } = await writeContractAndWait(request)

    if (
      args.aprAgent?.to &&
      args.aprAgent?.data &&
      isSameAddressLoose(account, args.initialOwner)
    ) {
      try {
        const aprHash = await clientWallet.sendTransaction({
          to: args.aprAgent.to as Address,
          data: args.aprAgent.data as `0x${string}`,
        })
        await publicWallet.waitForTransactionReceipt({ hash: aprHash, confirmations: 1 })
      } catch (error) {
        logError('aprAgent (addAgent) failed — pool owner must send it manually', error)
      }
    }

    return {
      vaultAddress,
      vaultIdentity,
      stakingAddress,
      txHash: hash,
    }
  }

  return { isAgent, orchestratorAddress, deployCreatePoolStaking }
}

/**
 * Steps 1–3 of useCreateStakingPool3643. Without a connected wallet only the BE pool row is created.
 */
export const createStakingPoolWithChain = async (
  payload: Parameters<typeof stakingErc3643Api.createPool>[0],
  account: Address | undefined,
  deploy: (
    account: Address,
    args: CreateStakingErc3643PoolResult
  ) => Promise<StakingErc3643CreateOnChainResult>
) => {
  const res = await stakingErc3643Api.createPool(payload)
  const args = res.data?.data
  if (!res.data?.success || !args?.stakingPoolId) {
    throw new Error(res.data?.message ?? 'Create pool failed')
  }

  if (!account) {
    return { args, onChain: null as StakingErc3643CreateOnChainResult | null }
  }

  const onChain = await deploy(account, args)
  return { args, onChain }
}
